import { Button, TextInput } from "@mantine/core";
import type React from "react";
import EmptyState from "../../shared/EmptyState";
import PhotoUpload from "../../shared/PhotoUpload";

export interface ClientProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  address?: string;
  profile_photo_url?: string;
  favorite_trainer?: { name: string } | null;
  [key: string]: unknown;
}

export interface ClientProfileEditData {
  first_name: string;
  last_name: string;
  phone_number: string;
  address: string;
  password: string;
  confirmPassword: string;
}

interface ClientProfileTabProps {
  profile: ClientProfileData | null;
  isEditingProfile: boolean;
  profileEditData: ClientProfileEditData;
  passwordError: string | null;
  loading: boolean;
  error: string | null;
  uploadingPhoto: boolean;
  onEditProfile: () => void;
  onCancel: () => void;
  onSave: () => void;
  onEditChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ClientProfileTab: React.FC<ClientProfileTabProps> = ({
  profile,
  isEditingProfile,
  profileEditData,
  passwordError,
  loading,
  error,
  uploadingPhoto,
  onEditProfile,
  onCancel,
  onSave,
  onEditChange,
  onPhotoUpload,
}) => (
  <div className="bg-card rounded-xl p-4 sm:p-6 shadow-lg border border-border min-h-[400px]">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
      <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
        My Profile
      </h2>
      {!isEditingProfile && (
        <Button onClick={onEditProfile} radius="md">
          Edit Profile
        </Button>
      )}
    </div>
    {loading ? (
      <div className="text-center text-[0.9375rem] text-muted-foreground py-12">
        Loading profile...
      </div>
    ) : error ? (
      <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
        {error}
      </div>
    ) : !profile ? (
      <EmptyState icon="Profile" description="Profile information not available" />
    ) : isEditingProfile ? (
      <div className="flex flex-col gap-4">
        <div className="mb-[30px]">
          <PhotoUpload
            photoUrl={profile.profile_photo_url}
            onUpload={onPhotoUpload}
            uploading={uploadingPhoto}
            size="md"
            uploadLabel="Change Photo"
          />
        </div>

        <div className="flex flex-col gap-[5px]">
          <label className="text-[0.9rem] font-semibold text-foreground">First Name</label>
          <TextInput
            name="first_name"
            value={profileEditData.first_name || profile.first_name}
            onChange={onEditChange}
          />
        </div>
        <div className="flex flex-col gap-[5px]">
          <label className="text-[0.9rem] font-semibold text-foreground">Last Name</label>
          <TextInput
            name="last_name"
            value={profileEditData.last_name || profile.last_name}
            onChange={onEditChange}
          />
        </div>
        <div className="flex flex-col gap-[5px]">
          <label className="text-[0.9rem] font-semibold text-foreground">Phone Number</label>
          <TextInput
            name="phone_number"
            value={profileEditData.phone_number || profile.phone_number}
            onChange={onEditChange}
          />
        </div>
        <div className="flex flex-col gap-[5px]">
          <label className="text-[0.9rem] font-semibold text-foreground">Address</label>
          <TextInput
            name="address"
            value={profileEditData.address || profile.address}
            onChange={onEditChange}
          />
        </div>
        <div className="flex flex-col gap-[5px]">
          <label className="text-[0.9rem] font-semibold text-foreground">
            New Password (optional)
          </label>
          <TextInput
            type="password"
            name="password"
            value={profileEditData.password}
            onChange={onEditChange}
            placeholder="Leave blank to keep current password"
          />
        </div>
        {profileEditData.password && (
          <div className="flex flex-col gap-[5px]">
            <label className="text-[0.9rem] font-semibold text-foreground">
              Confirm New Password
            </label>
            <TextInput
              type="password"
              name="confirmPassword"
              value={profileEditData.confirmPassword}
              onChange={onEditChange}
            />
          </div>
        )}
        {passwordError && (
          <div className="text-center text-[0.9375rem] text-destructive p-6 bg-destructive/10 border border-destructive/25 rounded-lg">
            {passwordError}
          </div>
        )}
        <div className="flex gap-2.5 mt-5 justify-end">
          <Button onClick={onSave} color="green">
            Save Changes
          </Button>
          <Button onClick={onCancel} color="gray">
            Cancel
          </Button>
        </div>
      </div>
    ) : (
      <div className="bg-muted rounded-xl p-4 sm:p-[30px] flex flex-col sm:flex-row items-center gap-5 border-2 border-border">
        <div className="mb-[30px]">
          <PhotoUpload
            photoUrl={profile.profile_photo_url}
            onUpload={onPhotoUpload}
            uploading={uploadingPhoto}
            size="lg"
            uploadLabel="Upload Photo"
          />
        </div>

        <div className="flex-1">
          <h3 className="text-2xl font-semibold text-foreground mb-[15px]">
            {profile.first_name} {profile.last_name}
          </h3>
          <p className="text-base text-muted-foreground mb-2">
            <strong>Email:</strong> {profile.email}
          </p>
          <p className="text-base text-muted-foreground mb-2">
            <strong>Phone:</strong> {profile.phone_number || "Not provided"}
          </p>
          <p className="text-base text-muted-foreground mb-2">
            <strong>Address:</strong> {profile.address || "Not provided"}
          </p>
          {profile.favorite_trainer && (
            <p className="text-base text-muted-foreground mb-2">
              <strong>Favorite Trainer:</strong> {profile.favorite_trainer.name}
            </p>
          )}
        </div>
      </div>
    )}
  </div>
);

export default ClientProfileTab;
